import React, { useState, useEffect } from 'react'
import { Box, Card, Flex, Group, Stack, Text, Col } from '@mantine/core'
import Image from 'next/image'
import TitleDivider from '../divider'
import Link from 'next/link'
import CountryInput from '../inputs/country_input'

const HitsCard = ({ combinedData, availablePlatforms, country, setCountry }) => {
  const [backdropSrc, setBackdropSrc] = useState('')
  const [isBackdropFallback, setIsBackdropFallback] = useState(false)
  const [posterStates, setPosterStates] = useState([])

  useEffect(() => {
    // Filtrar solo los hits con poster
    const hitsWithPoster = combinedData.filter((hit) => hit.poster)

    if (hitsWithPoster.length > 0) {
      // Establecer el backdrop del primer hit con poster
      setBackdropSrc(hitsWithPoster[0].backdrop || hitsWithPoster[0].poster)
      setIsBackdropFallback(false)

      // Establecer los estados de los posters, solo para hits con poster
      setPosterStates(
        hitsWithPoster.slice(0, 5).map((hit) => ({
          src: hit.poster,
          isFallback: false,
          hit: hit
        }))
      )
    } else {
      // Si no hay hits con poster, reiniciar los estados
      setBackdropSrc('/noimage.png')
      setIsBackdropFallback(true)
      setPosterStates([])
    }
  }, [country, combinedData])

  const handleBackdropError = () => {
    setBackdropSrc('/noimage.png')
    setIsBackdropFallback(true)
  }

  // Si no hay hits con poster, no renderizar nada
  if (posterStates.length === 0) return null

  return (
    <Col span={12} sm={6} md={4} lg={4} xl={4} p={5} sx={{ height: '613.5px' }}>
      <Stack spacing={8} mb={20} sx={{ gap: '0' }}>
        <Flex justify="space-between" align="center">
          <Box sx={{ width: '100%' }}>
            <TitleDivider
              titleText={
                <Flex align="center" gap={8}>
                  <Text component="span">
                    {availablePlatforms
                      ? `${availablePlatforms.platform_names[0].toUpperCase()} HITS IN`
                      : 'LOADING...'}
                  </Text>
                  <CountryInput
                    withLabel={false}
                    onChange={setCountry}
                    value={country}
                    filterCountries={[
                      'ID',
                      'AR',
                      'IT',
                      'PE',
                      'GB',
                      'KR',
                      'TH',
                      'CO',
                      'TR',
                      'US',
                      'CA',
                      'FR',
                      'BR',
                      'DE',
                      'ES',
                      'MX',
                      'AU',
                      'CL',
                      'IN',
                      'BE',
                      'PH',
                      'SE',
                      'DK',
                      'NO',
                      'ZA',
                      'NL'
                    ]}
                    style={{ maxWidth: '130px' }}
                  />
                </Flex>
              }
            />
          </Box>
        </Flex>
      </Stack>

      <Card
        sx={{
          overflow: 'visible',
          backgroundColor: '#0E1922',
          boxShadow: '0px -3px 0px 0px rgba(123,57,182,1) inset',
          height: '100%'
        }}
        p={0}
        w="100%"
      >
        <Card.Section
          sx={{
            width: '100%',
            margin: 0,
            padding: 0,
            height: '170px',
            position: 'relative'
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative'
            }}
          >
            <Image
              src={backdropSrc}
              onError={handleBackdropError}
              fill={!isBackdropFallback}
              width={isBackdropFallback ? 30 : undefined}
              height={isBackdropFallback ? 30 : undefined}
              style={{
                objectFit: isBackdropFallback ? 'contain' : 'cover',
                borderRadius: '4px'
              }}
              alt="movie poster"
            />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0px -3px 0px 0px rgba(123,57,182,1) inset',
              borderRadius: '4px'
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                padding: '5px'
              }}
            >
              <Text color="white" tt="uppercase" fw={700} size={12}>
                {posterStates[0]?.hit.title.length > 30
                  ? posterStates[0].hit.title.substring(0, 30) + '...'
                  : posterStates[0]?.hit.title}
              </Text>
              <Text color="white" tt="uppercase" fw={500} size={10}>
                {posterStates[0]?.hit.content_type} |{' '}
                {posterStates[0]?.hit.genres?.[0]} | {posterStates[0]?.hit.year}
              </Text>
            </Box>
            {posterStates[0]?.hit.content_type === 'Series' && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 10,
                  padding: '5px',
                  backgroundColor: 'rgba(123,57,182,1)',
                  borderRadius: '0 0 5px 5px',
                  display: 'flex',
                  placeContent: 'center'
                }}
                w={21}
                h={19}
              >
                <Text color="white" size={10} fw={700} p={0}>
                  TV
                </Text>
              </Box>
            )}
          </Box>
        </Card.Section>
        <Stack p="md">
          {posterStates.map((posterState, index) => (
            <Link
              href={`/${posterState.hit.content_type.toLowerCase()}/${
                posterState.hit.slug
              }`}
              passHref
              key={posterState.hit.uid}
            >
              <Flex align="center" gap={12} sx={{ cursor: 'pointer' }}>
                <Group spacing={0} sx={{ minWidth: '25%' }} align="baseline">
                  <Text
                    fw={800}
                    color="rgba(123,57,182,1)"
                    size="35px"
                    sx={{ lineHeight: 0.9 }}
                  >
                    {index + 1}
                  </Text>
                  <Box
                    sx={{
                      width: '45px',
                      height: '67px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '4px',
                      backgroundColor: posterState.isFallback
                        ? 'rgba(255,255,255,0.1)'
                        : 'transparent'
                    }}
                  >
                    <Image
                      src={posterState.src}
                      width={45}
                      height={67}
                      alt="poster"
                      style={{
                        borderRadius: '4px',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                </Group>
                <Stack spacing={0}>
                  <Flex align="center" gap={0}>
                    <Text
                      color="white"
                      tt="uppercase"
                      fw={400}
                      size={14}
                      className="text-ellipsis"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: 'inline-block',
                        width: '220px'
                      }}
                    >
                      {posterState.hit.title}
                    </Text>
                    {posterState.hit.score > 0 && (
                      <Text
                        size={10}
                        weight={700}
                        sx={{
                          backgroundColor: 'rgba(123,57,182,0.2)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          color: 'rgba(123,57,182,1)',
                          letterSpacing: '0.5px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0,
                          marginLeft: '4px'
                        }}
                      >
                        <Image
                          src="/scorestar.png"
                          width={10}
                          height={10}
                          alt="Score star"
                        />
                        {Number(posterState.hit.score).toFixed(1)}
                      </Text>
                    )}
                  </Flex>
                  <Text color="#7B39B6" tt="uppercase" fw={200} size={10}>
                    {posterState.hit.content_type} | {posterState.hit.genres?.[0]} |{' '}
                    {posterState.hit.year}
                  </Text>
                </Stack>
              </Flex>
            </Link>
          ))}
        </Stack>
      </Card>
    </Col>
  )
}

export default HitsCard
